import {
 filter, first, isEmpty, map,
} from 'lodash';
import * as React from 'react';

import { InfoCard } from '@frontend/app/components';
import {
 useListTikTokSettingsAccountsQuery, useTikTokSettingsDisconnectAccountMutation, useTikTokSettingsDisconnectAdAccountMutation, useListTikTokSettingsAdAccountsQuery, useTikTokSettingsUpdateAdAccountMutation,
 useClientFeatureEnabled,
 useTikTokSettingsUpdateTtoAccountMutation,
} from '@frontend/app/hooks';
import { LIST_TIKTOK_SETTINGS_ACCOUNTS, LIST_TIKTOK_SETTINGS_AD_ACCOUNTS } from '@frontend/app/queries';
import {
 Button, message, Modal, Space,
} from '@revfluence/fresh';
import { EarIcon, HandshakeIcon, TriangleExclamationIcon } from '@revfluence/fresh-icons/regular/esm';

import { ClientFeature } from '@frontend/app/constants';
import { useLocation } from 'react-router-dom';
import { useMessagingContext } from '@frontend/hooks';
import { useClientTiktokTtoAccountsQuery } from '@frontend/app/hooks/useClientTiktokTtoAccountsQuery';
import { useApplication } from '@frontend/applications/Shared/context/applicationContext';
import { ILoadingRows, TikTokAccountsTable } from '../components/TikTokAccountsTable';
import { TikTokAdAccountsTable } from '../components/TikTokAdAccountsTable';
import { useOAuthConnectTikTok } from '../hooks/useOAuthConnectTikTok';
import { useOAuthConnectTikTokAdAccount } from '../hooks/useOAuthConnectTikTokAdAccount';
import { Layout } from './Layout';
import { TikTokTtoAccountsTable } from '../components/TikTokTtoAccountsTable';

const {
 useCallback, useEffect, useState, useMemo,
} = React;

export interface AdAccountLoadingState {
  removingLoading?: true;
  sparkAdsLoading?: true;
}

export interface AdAccountTableLoadingRows {
  [key: string]: AdAccountLoadingState;
}

export interface TtoAccountLoadingState {
  removingLoading?: true;
  ttoCampaignLoading?: true;
}

export interface TtoAccountTableLoadingRows {
  [key: string]: TtoAccountLoadingState;
}

const DEFAULT_PAGING = {
  limit: 10,
  offset: 0,
};

enum ErrorReason {
  MISSING_PERMISSIONS = 'missing_permissions',
  NO_ACCOUNTS = 'no_accounts',
}

const RemoveTikTokAccountConfirmModal = (confirmAction: () => void) => {
  const content = (
    <p>
      Please note that after removing your TikTok account, we will no longer capture any data for social listening
      purposes. This means that we will not be able to gather insights or monitor any awareness or engagement related to
      your account.
    </p>
  );

  return Modal.confirm({
    title: 'Remove TikTok Account',
    content,
    okText: 'Remove',
    cancelText: 'Cancel',
    onOk: confirmAction,
  });
};

const RemoveTikTokAdAccountConfirmModal = (confirmAction: () => void) => {
  const content = (
    <p>
      Please note that after removing your TikTok Ad account, we will no longer be able to request posts to be used for Spark Ads.
    </p>
  );

  return Modal.confirm({
    title: 'Remove TikTok Ad Account',
    content,
    okText: 'Remove',
    cancelText: 'Cancel',
    onOk: confirmAction,
  });
};

export const TiktokSettingsPage = () => {
  const { showMessage } = useMessagingContext();
  const location = useLocation();
  const { clientId } = useApplication();

  useEffect(() => {
    const error = (new URLSearchParams(location.search).get('success')) == 'False';
    const errorReason = new URLSearchParams(location.search).get('error');
    let message;
    if (error) {
      switch (errorReason) {
        case ErrorReason.MISSING_PERMISSIONS:
          message = 'Unable to add account due to missing permissions. Select all required permissions and try again. Contact help@aspireiq.com if this continues.';
          break;
        case ErrorReason.NO_ACCOUNTS:
          message = 'Accounts not found. Ensure you are logged into the correct TikTok account and try again. Please contact help@aspireiq.com if this continues.';
          break;
        default:
          message = 'An unexpected error has occurred. Please contact help@aspireiq.com if this continues.';
      }

      showMessage({
        content: message,
        type: 'error',
      });
    }
  }, [location, showMessage]);

  const tiktokAdAccountsFeatureEnabled = useClientFeatureEnabled(ClientFeature.TIKTOK_SPARK_ADS);

  const tiktokTtoAccountsFeatureEnabled = useClientFeatureEnabled(ClientFeature.TIKTOK_TTO_ACCOUNTS);

  const [adAccountLoadingRows, setAdAccountLoadingState] = useState<AdAccountTableLoadingRows>({});

  const setAdAccountLoadingRows = (adAccountLoadingRow: AdAccountTableLoadingRows) => {
    setAdAccountLoadingState((state) => ({
      ...state,
      ...adAccountLoadingRow,
    }));
  };
  const clearAdAccountLoadingRows = useCallback(() => {
    setAdAccountLoadingState({});
  }, []);

  const [ttoAccountLoadingRows, setTtoAccountLoadingState] = useState<TtoAccountTableLoadingRows>({});

  const setTtoAccountLoadingRows = (ttoAccountLoadingRow: TtoAccountTableLoadingRows) => {
    setTtoAccountLoadingState((state) => ({
      ...state,
      ...ttoAccountLoadingRow,
    }));
  };
  const clearTtoAccountLoadingRows = useCallback(() => {
    setTtoAccountLoadingState({});
  }, []);

  const accountsQuery = useListTikTokSettingsAccountsQuery({
    variables: {
      paging: DEFAULT_PAGING,
    },
  });
  const { refetch: accountsRefetch } = accountsQuery;
  const accountsPaging = useMemo(() => accountsQuery.data?.tiktokSettingsListAccounts?.paging, [accountsQuery]);
  const accountsResults = useMemo(() => accountsQuery.data?.tiktokSettingsListAccounts?.results || [], [accountsQuery]);
  const [accountsLoadingRows, setAccountsLoadingState] = useState<ILoadingRows>({});
  const oauthConnectTikTokAccount = useOAuthConnectTikTok();
  const [disconnectAccount, disconnectAccountResult] = useTikTokSettingsDisconnectAccountMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: LIST_TIKTOK_SETTINGS_ACCOUNTS,
        variables: {
          paging: DEFAULT_PAGING,
        },
      },
    ],
  });

  const adAccountsQuery = useListTikTokSettingsAdAccountsQuery({
    variables: {
      paging: DEFAULT_PAGING,
    },
  });
  const { refetch: adAccountsRefetch } = adAccountsQuery;
  const adAccountsPaging = useMemo(() => adAccountsQuery.data?.tiktokSettingsListAdAccounts?.paging, [adAccountsQuery]);
  const adAccountsResults = useMemo(() => adAccountsQuery.data?.tiktokSettingsListAdAccounts?.results || [], [adAccountsQuery]);
  const oauthConnectTikTokAdAccount = useOAuthConnectTikTokAdAccount();
  const [disconnectAdAccount, disconnectAdAccountResult] = useTikTokSettingsDisconnectAdAccountMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: LIST_TIKTOK_SETTINGS_AD_ACCOUNTS,
        variables: {
          paging: DEFAULT_PAGING,
        },
      },
    ],
  });

  const [updateAdAccount, updateAdAccountResult] = useTikTokSettingsUpdateAdAccountMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: LIST_TIKTOK_SETTINGS_AD_ACCOUNTS,
        variables: {
          paging: DEFAULT_PAGING,
        },
      },
    ],
  });

  const [ttoAccountsQueryResult, ttoAccountsRefetch] = useClientTiktokTtoAccountsQuery({
    clientId,
    paging: DEFAULT_PAGING,
  }, {
    requestPolicy: 'network-only',
  });
  const ttoAccountsPaging = useMemo(() => ttoAccountsQueryResult.data?.clientTiktokTtoAccounts?.pageInfo, [ttoAccountsQueryResult]);
  const ttoAccountsResults = useMemo(() => ttoAccountsQueryResult.data?.clientTiktokTtoAccounts?.items || [], [ttoAccountsQueryResult]);
  const [updateTtoAccountResult, executeUpdateTtoAccount] = useTikTokSettingsUpdateTtoAccountMutation();

  useEffect(() => {
    const size = accountsResults.length || 0;
    const offset = accountsPaging?.offset || DEFAULT_PAGING.offset;
    if (size === 0 && offset > 0) {
      accountsRefetch({
        paging: {
          limit: accountsPaging?.limit || DEFAULT_PAGING.limit,
          offset: Math.max(offset - (accountsPaging?.limit || DEFAULT_PAGING.limit), 0),
        },
      });
    }
  }, [accountsResults, accountsPaging, accountsRefetch]);

  useEffect(() => {
    const size = adAccountsResults.length || 0;
    const offset = adAccountsPaging?.offset || DEFAULT_PAGING.offset;
    if (size === 0 && offset > 0) {
      adAccountsRefetch({
        paging: {
          limit: adAccountsPaging?.limit || DEFAULT_PAGING.limit,
          offset: Math.max(offset - (adAccountsPaging?.limit || DEFAULT_PAGING.limit), 0),
        },
      });
    }
  }, [adAccountsResults, adAccountsPaging, adAccountsRefetch]);

  useEffect(() => {
    const size = ttoAccountsResults.length || 0;
    const offset = ttoAccountsPaging?.offset || DEFAULT_PAGING.offset;
    if (size === 0 && offset > 0) {
      ttoAccountsRefetch({
        paging: {
          limit: ttoAccountsPaging?.limit || DEFAULT_PAGING.limit,
          offset: Math.max(offset - (ttoAccountsPaging?.limit || DEFAULT_PAGING.limit), 0),
        },
      });
    }
  }, [ttoAccountsResults, ttoAccountsPaging, ttoAccountsRefetch]);

  const someAccountQueryError = useMemo(() => !!accountsQuery.error?.message, [accountsQuery]);
  const someDisconnectAccountError = useMemo(() => disconnectAccountResult?.error?.message, [disconnectAccountResult]);
  const setLoadingAccountRows = (loadingRow: ILoadingRows) => {
    setAccountsLoadingState((state) => ({
      ...state,
      ...loadingRow,
    }));
  };

  const someAdAccountQueryError = useMemo(() => !!adAccountsQuery.error?.message, [adAccountsQuery]);
  const someDisconnectAdAccountError = useMemo(() => disconnectAdAccountResult?.error?.message, [disconnectAdAccountResult]);
  const someUpdateAdAccountError = useMemo(() => updateAdAccountResult?.error?.message, [updateAdAccountResult]);

  const someTtoAccountQueryError = useMemo(() => !!ttoAccountsQueryResult.error?.message, [ttoAccountsQueryResult]);
  const someUpdateTtoAccountError = useMemo(() => !!updateTtoAccountResult.error?.message, [updateTtoAccountResult.error]);

  const clearAccountsLoadingRows = useCallback(() => {
    setAccountsLoadingState({});
  }, []);

  useEffect(() => {
    if (someAccountQueryError || someDisconnectAccountError || someAdAccountQueryError || someDisconnectAdAccountError || someUpdateAdAccountError || someTtoAccountQueryError || someUpdateTtoAccountError) {
      message.error({
        icon: <TriangleExclamationIcon />,
        content:
          'We were unable to load your settings. Please try again later, please contact help@aspireiq.com if your problem persists.',
      });
    }
  }, [someAccountQueryError, someDisconnectAccountError, someAdAccountQueryError, someDisconnectAdAccountError, someUpdateAdAccountError, someTtoAccountQueryError, someUpdateTtoAccountError]);

  useEffect(() => {
    const hasAccountError = !!accountsQuery.error || !!disconnectAccountResult.error;
    const notAccountLoading = !(accountsQuery.loading || disconnectAccountResult.loading);
    const hasLoadingRows = !isEmpty(accountsLoadingRows);
    if ((hasAccountError || notAccountLoading) && hasLoadingRows) {
      clearAccountsLoadingRows();
    }
  }, [accountsQuery, disconnectAccountResult, clearAccountsLoadingRows, accountsLoadingRows]);

  useEffect(() => {
    const hasAdAccountError = !!adAccountsQuery.error || !!disconnectAdAccountResult.error || !!updateAdAccountResult.error;
    const notLoading = !(adAccountsQuery.loading || disconnectAdAccountResult.loading || updateAdAccountResult.loading);
    const hasLoadingRows = !isEmpty(adAccountLoadingRows);
    if ((hasAdAccountError || notLoading) && hasLoadingRows) {
      clearAdAccountLoadingRows();
    }
  }, [adAccountsQuery, disconnectAdAccountResult, updateAdAccountResult, clearAdAccountLoadingRows, adAccountLoadingRows]);

  useEffect(() => {
    const hasTtoAccountError = !!ttoAccountsQueryResult.error || !!updateTtoAccountResult.error;
    const notLoading = !(ttoAccountsQueryResult.fetching || updateTtoAccountResult.fetching);
    const hasLoadingRows = !isEmpty(ttoAccountLoadingRows);
    if ((hasTtoAccountError || notLoading) && hasLoadingRows) {
      clearTtoAccountLoadingRows();
    }
  }, [ttoAccountsQueryResult.error, updateTtoAccountResult.error, ttoAccountsQueryResult.fetching, updateTtoAccountResult.fetching, clearTtoAccountLoadingRows, ttoAccountLoadingRows]);

  const isFirstLoad = (accountsQuery.loading && isEmpty(accountsQuery?.data?.tiktokSettingsListAccounts?.results))
    || (adAccountsQuery.loading && isEmpty(adAccountsQuery?.data?.tiktokSettingsListAdAccounts?.results));
  if (isFirstLoad) {
    return null;
  }

  const selectedTiktokSparkAdsAccount = first(
    map(
      filter(
        adAccountsResults,
        (adAccount) => adAccount.sparkAds.enabled,
      ),
      (adAccount) => adAccount.username,
    ),
  );

  const selectedTiktokTtoCampaignAccount = first(
    map(
      filter(
        ttoAccountsResults,
        (ttoAccount) => ttoAccount.ttoCampaignSettings?.enabled,
      ),
      (ttoAccount) => ttoAccount.tiktokTtoAccount?.accountName,
    ),
  );

  const buttonText = tiktokTtoAccountsFeatureEnabled ? 'Add TikTok One and Ad Account' : 'Add TikTok Ad Account';

  const addTiktokAdAccountButton = !tiktokAdAccountsFeatureEnabled ? null : (
    <Button onClick={() => oauthConnectTikTokAdAccount()} type="primary">
      {buttonText}
    </Button>
  );

  const safeAccountsPaging = accountsPaging || { count: 0, limit: 10, offset: 0 };

  return (
    <Layout
      SettingsInfoCards={(
        <Space direction="horizontal" align="start" size="middle" wrap>
          <InfoCard
            title="Listening"
            description="Monitor and identify branded content by members that mention your brand on TikTok."
            icon={<EarIcon />}
            width={415}
          />
          {tiktokAdAccountsFeatureEnabled && (
          <InfoCard
            title="Spark Ads"
            description="Request permission from creators to create Spark Ads from UGC."
            icon={<HandshakeIcon />}
            width={415}
          />
)}
        </Space>
      )}
      AddTiktokAccountButton={(
        <Button onClick={() => oauthConnectTikTokAccount()} type="primary">
          Add TikTok Account
        </Button>
      )}
      TiktokAccountsTable={(
        <TikTokAccountsTable
          pagination={{
            onChange(page, size) {
              accountsRefetch({
                paging: {
                  limit: size,
                  offset: (page - 1) * size,
                },
              });
            },
            total: safeAccountsPaging.count,
            pageSize: safeAccountsPaging.limit,
            current: safeAccountsPaging.offset / safeAccountsPaging.limit + 1,
          }}
          loadingRows={accountsLoadingRows}
          data={accountsResults}
          removeAccount={(accountId) => {
            RemoveTikTokAccountConfirmModal(() => {
              setLoadingAccountRows({ [accountId]: true });
              disconnectAccount({
                variables: {
                  accountId,
                },
              });
            });
          }}
        />
      )}
      AddTiktokAdAccountButton={addTiktokAdAccountButton}
      TiktokAdAccountsTable={(
        <TikTokAdAccountsTable
          tiktokAdAccountsFeatureEnabled={tiktokAdAccountsFeatureEnabled}
          ttoAccountsClientFeatureEnabled={tiktokTtoAccountsFeatureEnabled}
          selectedTiktokSparkAdsAccount={selectedTiktokSparkAdsAccount || ''}
          pagination={{
            onChange(page, size) {
              adAccountsRefetch({
                paging: {
                  limit: size,
                  offset: (page - 1) * size,
                },
              });
            },
            total: adAccountsPaging?.count || 0,
            pageSize: adAccountsPaging?.limit || DEFAULT_PAGING.limit,
            current: ((adAccountsPaging?.offset || 0) / (adAccountsPaging?.limit || DEFAULT_PAGING.limit)) + 1,
          }}
          loadingRows={adAccountLoadingRows}
          data={adAccountsResults}
          removeAccount={(accountId) => {
            RemoveTikTokAdAccountConfirmModal(() => {
              setAdAccountLoadingRows({ [accountId]: { removingLoading: true } });
              disconnectAdAccount({
                variables: {
                  accountId,
                },
              });
            });
          }}
          updateAccount={(variables) => {
            setAdAccountLoadingRows({ [variables.accountId]: { sparkAdsLoading: true } });
            updateAdAccount({
              variables,
            });
          }}
        />
      )}
      TiktokTtoAccountsTable={(
        <TikTokTtoAccountsTable
          tiktokTtoAccountsFeatureEnabled={tiktokTtoAccountsFeatureEnabled}
          selectedTiktokTtoCampaignAccount={selectedTiktokTtoCampaignAccount || ''}
          pagination={{
            onChange(page, size) {
              ttoAccountsRefetch({
                paging: {
                  limit: size,
                  offset: (page - 1) * size,
                },
              });
            },
            total: ttoAccountsPaging?.count || 0,
            pageSize: ttoAccountsPaging?.limit || DEFAULT_PAGING.limit,
            current: (ttoAccountsPaging?.offset || 0) / (ttoAccountsPaging?.limit || DEFAULT_PAGING.limit) + 1,
          }}
          loadingRows={ttoAccountLoadingRows}
          data={ttoAccountsResults}
          updateAccount={(variables, loadingState) => {
            setTtoAccountLoadingRows({ [variables.tiktokTtoAccountId]: { [loadingState]: true } });
            executeUpdateTtoAccount({
              params: {
                clientId,
                tiktokTtoAccountId: variables.tiktokTtoAccountId,
                action: variables.action,
              },
            });
          }}
          clientId={clientId}
          refreshAdAccounts={() => {
            adAccountsRefetch({
              paging: {
                limit: adAccountsPaging?.limit || DEFAULT_PAGING.limit,
                offset: adAccountsPaging?.offset || DEFAULT_PAGING.offset,
              },
            });
          }}
        />
      )}
    />
  );
};
